import { z } from "zod";

/**
 * Modèle de configuration. Tout ce qui décrit une expérience est une donnée
 * éditée par la régie, jamais du code (dossier d'architecture, ch. 2.2).
 */

export const zCoords = z.object({
  lat: z.number().min(-90).max(90),
  lon: z.number().min(-180).max(180),
});
export type Coords = z.infer<typeof zCoords>;

export const zFix = zCoords.extend({
  /** Précision horizontale en mètres telle que rapportée par l'appareil. */
  accuracy: z.number().nonnegative(),
  /** Horodatage client, en millisecondes. */
  at: z.number().int().nonnegative(),
});
export type Fix = z.infer<typeof zFix>;

export const zRendezvous = z.object({
  name: z.string().min(1).max(80),
  landmark: z.string().max(120).default(""),
  address: z.string().max(160).default(""),
  coords: zCoords,
  /**
   * On y est déjà.
   *
   * Une tribu ouverte pour un événement — un festival, une salle, une soirée
   * où tout le monde est sous le même toit — n'a personne à faire marcher.
   * Le cadran d'approche et sa distance n'y veulent rien dire : « 12 m » ne
   * dit pas où regarder, et le rayon de trente mètres est déjà franchi avant
   * d'avoir commencé.
   *
   * Le point garde des coordonnées — le protocole en exige, et elles servent
   * de repère à l'écran collectif — mais l'application ne demande plus de
   * marcher : elle demande de se reconnaître, ici, tout de suite.
   */
  surPlace: z.boolean().optional(),
});
export type Rendezvous = z.infer<typeof zRendezvous>;

export const zTheme = z.object({
  id: z.string().min(1).max(40),
  /** Intitulé vu par le participant : « J'aime marcher ». */
  title: z.string().min(1).max(80),
  /** Nom de la tribu : « Bleue ». */
  tribeName: z.string().min(1).max(40),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  capacity: z.number().int().min(2).max(20).default(10),
  /** Seuil de formation ; par défaut la capacité. */
  threshold: z.number().int().min(2).max(20).optional(),
  rendezvous: zRendezvous,
  rendezvousAlt: zRendezvous.optional(),
  mission: z.string().max(160).default(""),
  signal: z.string().max(80).default(""),
  soundPattern: z.string().max(60).default("motif-01"),
  active: z.boolean().default(true),
});
export type Theme = z.infer<typeof zTheme>;

export const zQuestion = z.object({
  id: z.string().min(1).max(40),
  text: z.string().min(1).max(240),
  a: z.string().min(1).max(120),
  b: z.string().min(1).max(120),
  c: z.string().min(1).max(120),
});
export type Question = z.infer<typeof zQuestion>;

export const zAnswerChoice = z.enum(["a", "b", "c"]);
export type AnswerChoice = z.infer<typeof zAnswerChoice>;

export const zExperience = z.object({
  id: z.string().min(1).max(60),
  name: z.string().min(1).max(120),
  /** vertical = téléphones seuls ; horizontal = avec écran collectif 16:9. */
  format: z.enum(["vertical", "horizontal"]).default("vertical"),
  themes: z.array(zTheme).min(1).max(12),
  waitingQuestions: z.array(zQuestion).max(5).default([]),
  /**
   * Ancienne « dernière question » collective, remplacée par les points
   * communs. Conservée facultative pour relire les réglages déjà écrits.
   */
  finalQuestion: zQuestion.optional(),
  /**
   * Conservé pour les configurations existantes, mais le moteur ne rejoue
   * plus la question : le désaccord est une réponse, pas un échec.
   */
  finalAttempts: z.number().int().min(1).max(9).default(1),
  chat: z
    .object({
      /**
       * Garde-fou contre l'inondation, pas un quota affiché : le chat est
       * ouvert quinze minutes, on y écrit ce qu'on veut dans cette limite.
       */
      messagesPerPerson: z.number().int().min(1).max(100_000).default(40),
      /** Fenêtre du chat avant le rendez-vous : quinze minutes. */
      windowMs: z.number().int().min(60_000).max(3_600_000).default(900_000),
      /**
       * Durée pendant laquelle on peut écrire, comptée depuis l'ouverture du
       * chat. Absente : le chat se ferme à l'heure du rendez-vous (la ville).
       * `null` : il reste ouvert jusqu'à la fin (un événement qui le veut).
       */
      dureeMs: z.number().int().min(60_000).nullable().optional(),
    })
    .default({ messagesPerPerson: 40, windowMs: 900_000 }),
  /**
   * Rayon de réunion en mètres.
   *
   * Trente mètres tenaient de la mesure de laboratoire : un GPS de ville se
   * trompe couramment de cette distance, et une place, un porche ou un étage
   * la mangent tout entière. Soixante-quinze couvre le lieu de rendez-vous
   * plutôt que le point exact, sans pour autant valider un humain resté au
   * carrefour d'à côté.
   */
  gatherRadiusM: z.number().int().min(10).max(150).default(75),
  /** Durée de présence continue dans le rayon avant de déclarer la réunion. */
  gatherHoldMs: z.number().int().min(2_000).max(60_000).default(10_000),
  /** Délai avant que Gom Gom propose une alternative (V5 : 5 minutes). */
  alternativeAfterMs: z.number().int().min(60_000).max(1_800_000).default(300_000),
  /**
   * Délai avant qu'une place abandonnée (page fermée, réseau perdu) revienne
   * au groupe. Le jeton de reprise vaut pendant tout ce délai.
   */
  abandonGraceMs: z.number().int().min(5_000).max(3_600_000).default(600_000),
  /** Attente maximale de formation d'une tribu. */
  formationTimeoutMs: z.number().int().min(60_000).max(1_800_000).default(300_000),
  transparencyText: z.string().max(400).default(
    "Vos réponses servent uniquement à cette rencontre et disparaissent avec elle.",
  ),
  minAge: z.number().int().min(13).max(21).default(18),
});
export type Experience = z.infer<typeof zExperience>;

export const zSector = z.object({
  id: z.string().min(1).max(60),
  city: z.string().min(1).max(80),
  name: z.string().min(1).max(80),
  /** Anneaux d'un polygone simple : [ [lon, lat], ... ]. */
  polygon: z.array(z.tuple([z.number(), z.number()])).min(3),
  /** Un secteur non public n'apparaît pas dans la liste des participants (ch. 15.5). */
  publicSector: z.boolean().default(true),
  /**
   * Lieux publics et visibles où une tribu peut se donner rendez-vous.
   * Aucune saisie libre : qui crée une tribu choisit dans cette liste
   * (wireframe V1, écran 10).
   */
  places: z.array(zRendezvous).default([]),
});
export type Sector = z.infer<typeof zSector>;

export const zSessionState = z.enum(["planned", "open", "running", "closed"]);
export type SessionState = z.infer<typeof zSessionState>;

export const zSessionSummary = z.object({
  id: z.string(),
  /** Nom affiché. Pour une session à thème unique : « Tribu Bleue ». */
  name: z.string(),
  /** Identité de la tribu, quand la session n'en porte qu'une seule. */
  tribeName: z.string().nullable().default(null),
  /** Centre d'intérêt de la tribu : « J'aime marcher ». */
  subject: z.string().nullable().default(null),
  color: z.string().nullable().default(null),
  sectorId: z.string(),
  sectorName: z.string(),
  experienceId: z.string(),
  state: zSessionState,
  opensAt: z.number().int(),
  startsAt: z.number().int(),
  closesAt: z.number().int(),
  participants: z.number().int().nonnegative(),
  minParticipants: z.number().int().nonnegative(),
  maxParticipants: z.number().int().nonnegative(),
  /**
   * Le point de rendez-vous de la tribu.
   *
   * Publié avant même d'entrer, parce que c'est la deuxième chose qui fait
   * choisir : « à huit minutes à pied » ne se devine pas. Le lieu est public
   * et éclairé par construction, il n'y a donc rien à protéger — et c'est le
   * **téléphone** qui calcule la distance, avec sa position à lui. Le serveur
   * ne la reçoit pas : il donne un point, pas une carte de qui est où.
   */
  rendezvous: zCoords.nullable().default(null),
  /** Nom du lieu de rendez-vous : « Fontaine des Jacobins ». */
  lieu: z.string().nullable().default(null),
  /** Ouverture du chat de la tribu (rendez-vous moins la fenêtre de chat). */
  chatOpensAt: z.number().int().nullable().default(null),
});
export type SessionSummary = z.infer<typeof zSessionSummary>;

/** Vue publique d'une tribu, sans donnée individuelle. */
export const zTribePublic = z.object({
  themeId: z.string(),
  title: z.string(),
  tribeName: z.string(),
  color: z.string(),
  /** Humains dans la tribu en cours, s'il y en a une de vivante. */
  present: z.number().int().nonnegative(),
  /**
   * Humains dans la salle d'attente de ce thème, qui n'appartiennent à aucune
   * tribu. Distinct de `present` : une tribu peut être partie en chemin
   * pendant qu'un nouveau groupe se forme derrière elle.
   */
  waiting: z.number().int().nonnegative().default(0),
  /** Minimum pour démarrer. */
  threshold: z.number().int().positive(),
  /** Maximum au-delà duquel la tribu est fermée. */
  capacity: z.number().int().positive(),
  formed: z.boolean(),
  /**
   * Humains de la salle d'attente qui ont répondu à toutes les questions.
   * Le démarrage les attend : les réponses servent aux points communs, et
   * partir sans elles prive tout le monde de la fin.
   */
  questionsDone: z.number().int().nonnegative().default(0),
  /**
   * Humains de la salle d'attente qui se sont déclarés prêts à partir. La
   * tribu ne se forme que lorsque tous le sont : appuyer sur « Je suis prêt »
   * n'emmène personne d'autre.
   */
  ready: z.number().int().nonnegative().default(0),
  /** Vrai dès que le minimum est atteint et que tout le monde a répondu. */
  canStart: z.boolean(),
  gathered: z.boolean(),
});
export type TribePublic = z.infer<typeof zTribePublic>;

/**
 * Les moments d'une tribu, dans l'ordre de la frise.
 *
 *  - `inscriptions` : on rejoint, Hoa pose ses questions ;
 *  - `chat` : quinze minutes avant le rendez-vous, pour tout le monde en même
 *    temps ; qui arrive en retard lit l'historique ;
 *  - `rendezvous` : l'heure est passée, le chat se relit, on marche ;
 *  - `reunie` : au moins deux humains sont arrivés ;
 *  - `annulee` : le minimum n'était pas atteint à l'ouverture du chat.
 */
export const zPhaseTribu = z.enum(["inscriptions", "chat", "rendezvous", "reunie", "annulee"]);
export type PhaseTribu = z.infer<typeof zPhaseTribu>;

/** Vue privée d'une tribu, réservée à ses membres. */
export const zTribePrivate = z.object({
  id: z.string(),
  themeId: z.string(),
  title: z.string(),
  tribeName: z.string(),
  color: z.string(),
  signal: z.string(),
  mission: z.string(),
  rendezvous: zRendezvous,
  members: z.array(
    z.object({
      pid: z.string(),
      pseudo: z.string(),
      /** C'est moi : ma pastille se distingue dans la liste. */
      moi: z.boolean().default(false),
      /** Arrivé au rendez-vous. */
      arrive: z.boolean().default(false),
      /**
       * Soirées allées au bout, établies par des reçus signés. `null` : la
       * régie a éteint l'affichage, l'écran ne montre rien.
       */
      rencontres: z.number().int().nonnegative().nullable().default(0),
      /** L'empreinte de sa photo de profil (`/api/photos/<empreinte>`), ou `null`. */
      photo: z.string().nullable().optional(),
    }),
  ),
  minimum: z.number().int().positive(),
  maximum: z.number().int().positive(),
  /** Ouverture du chat : quinze minutes avant le rendez-vous. */
  chatOpensAt: z.number().int(),
  /** Heure du rendez-vous ; le chat passe en lecture seule. */
  rdvAt: z.number().int(),
  /**
   * Fin du chat quand elle diffère du rendez-vous (événements) : un instant,
   * ou `null` s'il reste ouvert jusqu'à la fin. Absent : fermé au rendez-vous.
   */
  chatFinA: z.number().int().nullable().optional(),
  phase: zPhaseTribu,
  /** Nombre d'humains arrivés sur place. */
  arrives: z.number().int().nonnegative(),
  /**
   * La régie autorise la couleur de la tribu en plein écran à l'arrivée
   * (chacun peut aussi la couper dans ses paramètres). Absent : oui.
   */
  couleurArrivee: z.boolean().optional(),
});
export type TribePrivate = z.infer<typeof zTribePrivate>;
